import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useToast } from '@components/common/toast';
import profileConditionsSchema from '@components/profile/profileOverlay/overlays/prefer/profileConditionsSchema';
import { useInsightAutoSaved } from '@repository/insight/useInsight';
import useProfileConditions, {
  useSaveJobGroups,
  useSaveLocations,
  useSaveSalary,
  useSaveWorkingPreference,
} from '@repository/profiles/useProfileConditions';
import { useJobGroupsWithTitles } from '@repository/codes/useCodes';
import Location from './locationGroup';
import Salary from './salaryGroup';
import JobGroup from './jobGroup';
import Experience from './experienceGroup';
import Result from './result';
import RollingMessage from '../rollingMessage';
import MatchingConditionSaveRq from '@domain/rq/profile/matchingConditions/MatchingConditionSaveRq';
import MatchingConditionLocationRq from '@domain/rq/profile/matchingConditions/MatchingConditionLocationRq';
import MatchingConditionJobGroupRq from '@domain/rq/profile/matchingConditions/MatchingConditionJobGroupRq';
import MatchingConditionSalaryRq from '@domain/rq/profile/matchingConditions/MatchingConditionSalaryRq';
import MatchingConditionWorkingPreferenceRq from '@domain/rq/profile/matchingConditions/MatchingConditionWorkingPreferenceRq';
import ProfileConditionsFormVO from '@domain/vo/profile/ProfileConditionsFormVO';
import classnames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

export type PreferTabType = 'jobGroup' | 'location' | 'salary' | 'welfare' | 'working_preference' | null;
export type PreferConditionsFormType = 'jobGroups' | 'locations' | 'salary' | 'welfareKeywords';

const preferDefaultValues: RecursivePartial<ProfileConditionsFormVO> = {
  jobGroups: [],
  locations: [],
  salary: {},
  welfareKeywords: [],
  workingPreference: '',
};

const Step1 = ({}: {}) => {
  const { query } = useRouter();
  const Toast = useToast();
  const [selectedTabType, setSelectedTabType] = useState<PreferTabType>(null);
  const [isExperienceFilled, setIsExperienceFilled] = useState(false);

  const [jobGroupClicked, setJobGroupClicked] = useState(false);
  const [jobTitleClicked, setJobTitleClicked] = useState(false);
  const [locationClicked, setLocationClicked] = useState(false);
  const [salaryClicked, setSalaryClicked] = useState(false);
  const [experienceClicked, setExperienceClicked] = useState(false);

  const totalSteps = 5; // 총 스텝 수

  const getProgressWidth = () => {
    const stepsCompleted = [jobGroupClicked, jobTitleClicked, locationClicked, salaryClicked, experienceClicked].filter(
      Boolean,
    ).length;
    return `${(stepsCompleted / totalSteps) * 100}%`;
  };

  const { data: insightAutoSaved, refetch: insightAutoSavedRefetch } = useInsightAutoSaved({
    enabled: true,
  });
  const { data: jobGroupsWithTitles } = useJobGroupsWithTitles();
  const { mutateAsync: saveJobGroups } = useSaveJobGroups();
  const { mutateAsync: saveLocations } = useSaveLocations();
  const { mutateAsync: saveSalary } = useSaveSalary();
  const { mutateAsync: saveWorkingPreference } = useSaveWorkingPreference();
  const { refetch: refetchProfileCondition } = useProfileConditions(true);

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isNextDisabled, setIsNextDisabled] = useState<boolean>(true);
  const [isPreviousDisabled, setIsPreviousDisabled] = useState<boolean>(true);

  const handleNextStep = () => {
    if (currentStep === 3 && isExperienceFilled) {
      handleSubmit(onSubmit)();
    }

    if (!isNextDisabled) {
      setCurrentStep((prevStep) => prevStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (!isPreviousDisabled) {
      setCurrentStep((prevStep) => (prevStep > 0 ? prevStep - 1 : 0));
    }
  };

  useEffect(() => {
    setIsPreviousDisabled(currentStep === 0);
  }, [currentStep]);

  const saveSuccess = async () => {
    await refetchProfileCondition();
    if (insightAutoSaved?.auditCount) await insightAutoSavedRefetch();
    setSelectedTabType(null);
  };

  const methods = useForm<ProfileConditionsFormVO>({
    mode: 'onChange',
    defaultValues: preferDefaultValues as ProfileConditionsFormVO,
    resolver: yupResolver(profileConditionsSchema) as any,
  });

  const { handleSubmit, control, watch } = methods;
  const watchedValues = useWatch({ control });

  const isSelectedTab = (preferTabType: PreferTabType) => preferTabType === selectedTabType;

  const isError = (vo: ProfileConditionsFormVO) => {
    if (isSelectedTab('jobGroup') && vo.jobGroups.length < 1) return true;
    if (isSelectedTab('location') && vo.locations.length < 1) return true;
    if (isSelectedTab('salary') && !vo.salary) return true;
    return false;
  };

  const onSubmit = async (vo: ProfileConditionsFormVO) => {
    const rq = new MatchingConditionSaveRq(Object.assign(vo, { sourcePage: query.source_page }));
    if (isError(vo)) return Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
    if (currentStep === 3 && isExperienceFilled) {
      await saveJobGroups(new MatchingConditionJobGroupRq(rq));
      await saveLocations(new MatchingConditionLocationRq(rq));
      await saveSalary(new MatchingConditionSalaryRq(rq));
      await saveWorkingPreference(new MatchingConditionWorkingPreferenceRq(rq), { onSuccess: saveSuccess });
    }
  };

  useEffect(() => {
    if (currentStep === 0 && watchedValues.jobGroups && watchedValues.jobGroups.length > 0) {
      setIsNextDisabled(false);
    } else if (currentStep === 1 && watchedValues.locations && watchedValues.locations.length > 0) {
      setIsNextDisabled(false);
    } else if (currentStep === 2 && watchedValues.salary && Object.keys(watchedValues.salary).length > 0) {
      setIsNextDisabled(false);
    } else if (currentStep === 3 && isExperienceFilled) {
      setIsNextDisabled(false);
    } else {
      setIsNextDisabled(true);
    }
  }, [currentStep, watchedValues, isExperienceFilled, setIsNextDisabled]);

  const result = useMemo(() => {
    const { jobGroupCode, jobTitleCodes } = watchedValues.jobGroups?.[0] || {};
    const { name, jobTitles } = jobGroupsWithTitles?.find((item) => item.code === jobGroupCode) || {};
    const jobTitle = jobTitleCodes?.map((code) => jobTitles?.find((item) => item.code === code)?.name).join(', ');

    return {
      jobGroup: name,
      jobTitle,
      locations: watchedValues.locations?.join(', '),
      salary: watchedValues.salary?.label,
      workingPreference: watchedValues.workingPreference,
    };
  }, [watchedValues, jobGroupsWithTitles]);
  return (
    <div className={cx('wrap')}>
      <FormProvider {...methods}>
        <form className={cx('enterInner')}>
          <div className={cx('preferredWrap')}>
            {/* 프로그레스바 */}
            <div className={cx('progressBar')}>
              <div className={cx('progress')} style={{ width: getProgressWidth() }} />
            </div>

            {/* 선호정보 스텝 [직군직무, 위치, 연봉, 경력] */}
            <div className={cx('preferredList')}>
              {currentStep === 0 && (
                <JobGroup
                  name={'jobGroups'}
                  jobGroupsWithTitles={jobGroupsWithTitles}
                  setJobGroupClicked={setJobGroupClicked}
                  setJobTitleClicked={setJobTitleClicked}
                />
              )}
              {currentStep === 1 && <Location name={'locations'} setLocationClicked={setLocationClicked} />}
              {currentStep === 2 && <Salary name={'salary'} setSalaryClicked={setSalaryClicked} />}
              {currentStep === 3 && (
                <Experience
                  name={'experience'}
                  setIsExperienceFilled={setIsExperienceFilled}
                  setExperienceClicked={setExperienceClicked}
                />
              )}
              {currentStep === 4 && <Result name={'result'} result={result} />}
            </div>
          </div>

          {currentStep === 4 ? null : (
            <div className={cx('fixedArea')}>
              <RollingMessage visible={true} />
              <div className={cx('btnArea')}>
                {currentStep === 4 ? null : (
                  <button
                    type="button"
                    className={cx('btn', 'btnBack')}
                    onClick={handlePreviousStep}
                    disabled={isPreviousDisabled}
                  >
                    이전
                  </button>
                )}
                <button
                  type="button"
                  className={cx('btn', 'btnNext')}
                  onClick={handleNextStep}
                  disabled={isNextDisabled}
                >
                  {currentStep === 3 ? '프로필 작성 완료' : '다음'}
                </button>
              </div>
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
};

export default Step1;
